// Host detection for the raisedbeef.com desktop marketing/home site.
//
// This repo serves several surfaces by hostname. The apex raisedbeef.com
// (and www) is the free desktop "front gate", distinct from the Clara app
// (app.raisedbeef.com) and the markets terminal (markets.raisedbeef.ai).

// The Cattle Market Central demo hosts serve the SAME site as raisedbeef.com, rebranded (src/brand).
// Without them here an unknown host fell through to the markets terminal (owner, 2026-10-21).
export const RAISEDBEEF_HOME_HOSTS = [
  "raisedbeef.com",
  "www.raisedbeef.com",
  "cattlemarketcentral.raisedbeef.com",
  "cattlemarketcentral.com",
  "www.cattlemarketcentral.com",
];

export function isRaisedBeefHomeHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  if (RAISEDBEEF_HOME_HOSTS.includes(h)) return true;
  // Local/dev preview override: ?site=home
  try {
    if (new URLSearchParams(window.location.search).get("site") === "home") return true;
  } catch {
    /* ignore */
  }
  return false;
}

export const LOBBY_HOSTS = ["cattleticker.raisedbeef.com"];

/**
 * Where a "Home" link goes: "/" — except on the lobby host, where "/" IS the
 * lobby board, so Home has to name the website (owner, 2026-10-18: after
 * Cattle Ticker, Home "doesn't go back to raisedbeef.com"). Every other page
 * path on that host is sent to raisedbeef.com by vercel.json; "/" cannot be.
 *
 * Hostname only, not ?site=lobby, and "/" when there is no window: prerendered
 * pages hydrate, and both of those keep the server and client markup equal.
 * The lobby host itself is never prerendered (main.tsx, createRoot).
 */
export function siteHomeHref(
  hostname: string | null = typeof window === "undefined" ? null : window.location.hostname,
): string {
  return hostname !== null && LOBBY_HOSTS.includes(hostname) ? "https://cattlemarketcentral.com/" : "/";
}

/** The lobby touch screen (cattleticker.raisedbeef.com). Preview: ?site=lobby. */
export function isLobbyHost(): boolean {
  if (typeof window === "undefined") return false;
  if (LOBBY_HOSTS.includes(window.location.hostname)) return true;
  try {
    return new URLSearchParams(window.location.search).get("site") === "lobby";
  } catch {
    return false;
  }
}

/**
 * Path to the barn sale report for a DB-exact barn name.
 *
 * Owner call 2026-08-01: every barn click site-wide lands on the same
 * publication-style sale report, on every host — it is public and standalone.
 * That still holds. What changed is WHICH report.
 *
 * ── /live, NOT /preview (2026-09-13) ─────────────────────────────────────
 * `/barn/:slug/live` is the same report in the site's current UI: same hooks,
 * same RPCs, same numbers as /preview — only the presentation differs. The
 * owner found the leaderboard still pointing at the old one ("the barn links
 * still go to the old live pages"), which was true of every caller here.
 *
 * The leaderboard's own links were REMOVED rather than repointed, on the
 * owner's call — a leaderboard is a price board, not a directory. These
 * callers are different: on /sales and the live map the barn name IS the
 * navigation, so they are repointed.
 *
 * ⚠️ THIS PASSES A NAME, NOT A SLUG, and that is why `resolveBarnSlug` was
 * taught to accept both. A name has no `xx-` state prefix; before that change
 * this path would have 404'd every link it built.
 */
export function rbBarnPath(barnName: string): string {
  return `/barn/${encodeURIComponent(barnName)}/live`;
}

/** Public boxed beef page path — /boxed-beef on the raisedbeef.com host
 *  (mounted outside the auth Layout there); /rb/boxed-beef everywhere else
 *  (the platform hub owns /boxed-beef behind sign-in on those hosts). */
export function rbBoxedBeefPath(): string {
  return isRaisedBeefHomeHost() ? "/boxed-beef" : "/rb/boxed-beef";
}

/** Public Cattle on Feed page path (same host rule as rbBoxedBeefPath). */
export function rbCofPath(): string {
  return isRaisedBeefHomeHost() ? "/cattle-on-feed" : "/rb/cattle-on-feed";
}

/** Public Fed Cattle page path (same host rule as rbBoxedBeefPath). */
export function rbFedCattlePath(): string {
  return isRaisedBeefHomeHost() ? "/fed-cattle" : "/rb/fed-cattle";
}
